import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";
import { Paper, Button, TextField, Typography, Container, Alert, CircularProgress } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useTranslation } from "react-i18next";
import { Helmet } from 'react-helmet-async';

import FormError from "../components/Forms/FormError";
import { requestPasswordReset } from "../api/auth";

const ForgotPassword = () => {
    const { t } = useTranslation();

    // State grouped at top
    const [errorMessage, setErrorMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    // Schema definition - uses translation inside component (good for t)
    const loginSchema = yup.object().shape({
        email: yup
            .string()
            .trim()
            .lowercase()
            .email(t("errors.email_invalid"))
            .required(t("errors.email_required"))
    });

    // React-hook-form setup
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(loginSchema),
    });

    // Handle form submission
    const onSubmit = async (data) => {
        setSubmitting(true);
        setErrorMessage("");  // Clear previous error on new submit
        setSubmitError(false);
        setSubmitSuccessful(false);

        try {
            const response = await requestPasswordReset(data.email);
            if (response) {
                setSubmitSuccessful(true);
            } else {
                setSubmitError(true);
                setErrorMessage(t("errors.email_not_found"));
            }
        } catch (error) {
            setSubmitError(true);
            setErrorMessage(error.response?.data?.message || t("errors.err"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t("seo.title.forgot_password")}</title>
                <meta property="og:title" content={t('seo.title.forgot_password')} />

                <meta name="robots" content="noindex" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Container
                maxWidth="lg"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '80vh'
                }}
            >
                {submitSuccessful ?
                    <Alert icon={<MarkEmailReadIcon fontSize="inherit" />} severity="success">
                        {t("reset_password.forgot_page_success")}
                    </Alert>
                    :
                    <Paper
                        sx={{
                            width: '100%',
                            maxWidth: '400px',
                            p: 3,
                            border: { xs: "none", sm: "1px solid rgb(226, 234, 243)" },
                            bgcolor: { xs: "none", sm: '#fcfcfc' },
                        }}
                    >
                        <Typography color="primary" variant="h2" sx={{ fontWeight: "bold", mb: 2, textAlign: 'left' }}>
                            {t("reset_password.forgot_page_title")}
                        </Typography>

                        {errorMessage && (
                            <FormError errorMessage={errorMessage} />
                        )}

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <TextField
                                label={t("forms.email")}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                fullWidth
                                color='secondary'
                                size="large"
                                sx={{
                                    mt: 2, mb: 1,
                                    // STRIPE SHADOW
                                    // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                                    '&:hover': {
                                        backgroundColor: 'secondary.light',
                                        // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                                    }
                                }}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    t("reset_password.forgot_page_submit")
                                )}
                            </Button>
                        </form>
                    </Paper>
                }
            </Container >
        </>
    );
};

export default ForgotPassword;
